import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, formatTs, formatLatency } from './utils';

const LOG_WINDOW_OPTIONS = [
  { value: 1, label: 'Past 1 day' },
  { value: 3, label: 'Past 3 days' },
  { value: 7, label: 'Past 7 days' },
  { value: 14, label: 'Past 14 days' },
];

export const LogsTab = ({ visible, authHeaders }) => {
  const [opsLogsDays, setOpsLogsDays] = useState(7);
  const [opsLogsSummary, setOpsLogsSummary] = useState(null);
  const [opsLogsLoading, setOpsLogsLoading] = useState(false);
  const [opsLogsError, setOpsLogsError] = useState(null);

  const loadOpsLogs = useCallback(async (days = opsLogsDays) => {
    setOpsLogsError(null);
    setOpsLogsLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/logs/summary?days=${encodeURIComponent(days)}`,
        { headers: authHeaders() },
      );
      setOpsLogsSummary(res.data || null);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Failed to load logs summary';
      setOpsLogsError(msg);
      setOpsLogsSummary(null);
    } finally {
      setOpsLogsLoading(false);
    }
  }, [authHeaders, opsLogsDays]);

  useEffect(() => {
    if (!visible) return;
    loadOpsLogs(opsLogsDays);
  }, [visible, opsLogsDays, loadOpsLogs]);

  return (
    <>
      {opsLogsError && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {opsLogsError}
        </div>
      )}

      <div className="AdminPortal-toolbar AdminPortal-toolbar--split">
        <div className="AdminPortal-statsToolbarLeft">
          <label className="AdminPortal-rangeField">
            <span className="AdminPortal-rangeFieldLabel">Log window</span>
            <select
              className="AdminPortal-select AdminPortal-rangeSelect"
              value={opsLogsDays}
              onChange={(e) => setOpsLogsDays(Number(e.target.value))}
              aria-label="Filter logs by time window"
            >
              {LOG_WINDOW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <p className="AdminPortal-statsMeta">
            {opsLogsSummary?.window?.startIso && (
              <>
                Window:{' '}
                <time dateTime={opsLogsSummary.window.startIso}>
                  {formatTs(opsLogsSummary.window.startIso)}
                </time>
                {' '}→{' '}
                <time dateTime={opsLogsSummary.window.endIso}>
                  {formatTs(opsLogsSummary.window.endIso)}
                </time>
                {opsLogsSummary.region && (
                  <>
                    {' · '}
                    Region: <strong>{opsLogsSummary.region}</strong>
                  </>
                )}
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          className="ChatButton"
          onClick={() => loadOpsLogs(opsLogsDays)}
          disabled={opsLogsLoading}
        >
          Refresh
        </button>
      </div>

      {opsLogsLoading && !opsLogsSummary && (
        <p className="AdminPortal-intro">Loading logs summary…</p>
      )}

      {opsLogsSummary && !opsLogsSummary.enabled && (
        <section className="AdminPortal-section">
          <h2 className="AdminPortal-sectionTitle">Logs are not configured yet</h2>
          <p className="AdminPortal-sectionLead">
            CloudWatch log groups are not configured for this backend environment.
            Add `CLOUDWATCH_LOG_GROUPS` or `EB_ENV_NAME` to backend environment variables and refresh.
          </p>
          {opsLogsSummary?.notes?.length > 0 && (
            <div className="AdminPortal-tableWrap">
              <table className="AdminPortal-table">
                <thead>
                  <tr>
                    <th scope="col">Setup notes</th>
                  </tr>
                </thead>
                <tbody>
                  {opsLogsSummary.notes.map((note, idx) => (
                    <tr key={idx}>
                      <td>{note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {opsLogsSummary && opsLogsSummary.enabled && (
        <>
          <section className="AdminPortal-section" aria-labelledby="logs-overview-heading">
            <h2 id="logs-overview-heading" className="AdminPortal-sectionTitle">
              Logs overview
            </h2>
            <p className="AdminPortal-sectionLead">
              Snapshot of operational health from backend logs in the selected time window.
            </p>
            <div className="AdminPortal-statGrid">
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Total log events</span>
                <span className="AdminPortal-statValue">{opsLogsSummary.totals.totalLogEvents}</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Errors</span>
                <span className="AdminPortal-statValue">{opsLogsSummary.totals.error}</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Warnings</span>
                <span className="AdminPortal-statValue">{opsLogsSummary.totals.warn}</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Chat requests completed</span>
                <span className="AdminPortal-statValue">{opsLogsSummary.totals.chatRequests}</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Average chat latency</span>
                <span className="AdminPortal-statValue">{formatLatency(opsLogsSummary.totals.avgChatLatencyMs)}</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">P95 chat latency</span>
                <span className="AdminPortal-statValue">{formatLatency(opsLogsSummary.totals.p95ChatLatencyMs)}</span>
              </div>
            </div>
          </section>

          <section className="AdminPortal-section" aria-labelledby="logs-events-heading">
            <h2 id="logs-events-heading" className="AdminPortal-sectionTitle">
              Top events
            </h2>
            <div className="AdminPortal-tableWrap">
              <table className="AdminPortal-table">
                <thead>
                  <tr>
                    <th scope="col">Event</th>
                    <th scope="col">Level</th>
                    <th scope="col">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {opsLogsSummary.topEvents.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="AdminPortal-tableEmpty">
                        No events found in this period.
                      </td>
                    </tr>
                  ) : (
                    opsLogsSummary.topEvents.map((row, idx) => (
                      <tr key={`${row.event}-${idx}`}>
                        <td className="AdminPortal-mono">{row.event}</td>
                        <td>{row.level}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="AdminPortal-section" aria-labelledby="logs-issues-heading">
            <h2 id="logs-issues-heading" className="AdminPortal-sectionTitle">
              Recent warnings and errors
            </h2>
            <div className="AdminPortal-tableWrap">
              <table className="AdminPortal-table">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Level</th>
                    <th scope="col">Event</th>
                  </tr>
                </thead>
                <tbody>
                  {opsLogsSummary.recentIssues.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="AdminPortal-tableEmpty">
                        No warnings or errors in this window.
                      </td>
                    </tr>
                  ) : (
                    opsLogsSummary.recentIssues.map((issue, idx) => (
                      <tr key={`${issue.timestamp}-${idx}`}>
                        <td>{formatTs(issue.timestamp)}</td>
                        <td>{issue.level}</td>
                        <td className="AdminPortal-mono">{issue.event}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
};
